import '~/renderer/utils/analytics';
import 'react-toastify/dist/ReactToastify.css';

import * as React from 'react';
import { Helmet } from 'react-helmet';
import { I18nextProvider } from 'react-i18next';
import { Provider } from 'react-redux';
import { ToastContainer, Zoom } from 'react-toastify';
import { Global } from '@emotion/core';
import { FlameGlobalStyles, FlameTheme, flameTheme } from '@lightspeed/flame/Core';
import { ThemeProvider } from 'emotion-theming';
import pickBy from 'lodash/pickBy';
import qs from 'qs';

import { cleanChannelName } from '~/main/helpers/clean-channel-name';
import { APP_NAME, HUB_EVENTS } from '~/main/namespace';
import i18n from '~/main/utils/i18n';
import { LoggerScope } from '~/main/utils/logger';
import { useEventListener } from '~/renderer/hooks/.';
import store from '~/renderer/store';
import { cfdActions } from '~/renderer/store/cfd';
import { connectionActions } from '~/renderer/store/connection';
import { isClientSideReady, isCustomerCFD, staticPath } from '~/renderer/utils';
import { getConfig } from '~/renderer/utils/config';
import contentSecurityPolicy from '~/renderer/utils/csp';
import Logger from '~/renderer/utils/logger';

import type { Options } from '~/renderer/store/cfd/types';

window.i18n = window.i18n || i18n;

const globalStyles = `
body,
html {
  min-height: 100vh;
}

#root {
  width: 100vw;
  min-height: 100vh;
}

button {
  outline: none !important;
}
`;

const breakpoints = ['576px', '768px', '992px'];

type Params = Partial<Options & { channelName: string; routeTo: string }>;

function handleNewQueryParams(params: Params) {
  const { options, background, logo } = getConfig(['options', 'background', 'logo']);
  store.dispatch(
    cfdActions.updateOptions(
      pickBy({ background, logo, ...options, ...params }, value => value !== undefined),
    ),
  );

  let replaceUrl = window.location.pathname;

  if (params.channelName) {
    const channelName = cleanChannelName(params.channelName);
    store.dispatch(connectionActions.updateConnectionChannelName(channelName));
    replaceUrl += `?channelName=${channelName}${window.location.hash}`;
  }

  if (params.routeTo) {
    // @Note temporary replace for receipt printers route from Retail /devices page
    if (params.routeTo === 'printers') {
      params.routeTo = 'printers/receipts';
    }
    window.location.replace(`#${params.routeTo}`);
  } else if (
    // Do not re-route the customer CFD (phones)
    !isCustomerCFD
  ) {
    window.history.replaceState({}, document.title, replaceUrl);
  }
}

const App: React.FC = ({ children }) => {
  useEventListener(HUB_EVENTS.QUERY_PARAMS, (_, { params }) => {
    handleNewQueryParams(params);
    Logger({ scope: LoggerScope.ELECTRON }).debug({
      msg: 'Applying new query parameters to the app',
      params,
    });
  });

  React.useEffect(() => {
    if (!isClientSideReady()) return;
    const queryParams = qs.parse(window.location.search, { ignoreQueryPrefix: true });
    handleNewQueryParams(queryParams);
  }, []);

  return (
    <>
      <noscript>Please enable Javascript to use the {APP_NAME}.</noscript>
      <Helmet>
        <title>{APP_NAME}</title>
        <meta name="description" content={APP_NAME} />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <meta name="theme-color" content="#ED5153" />
        <meta httpEquiv="Cache-Control" content="no-cache, no-store, must-revalidate" />
        <meta httpEquiv="Pragma" content="no-cache" />
        <meta httpEquiv="Content-Security-Policy" content={contentSecurityPolicy} />
        <meta httpEquiv="Expires" content="0" />
        <link rel="shortcut icon" type="image/x-icon" href={`${staticPath}/build/icon.ico`} />
        <link
          href="https://fonts.googleapis.com/css?family=Lato:400,700&subset=latin-ext"
          rel="stylesheet"
        />
      </Helmet>
      <I18nextProvider i18n={window.i18n}>
        <Provider store={store}>
          <FlameTheme themeOverrides={{ breakpoints }}>
            <ThemeProvider theme={{ ...flameTheme, breakpoints }}>
              <FlameGlobalStyles />
              <ToastContainer limit={2} transition={Zoom} newestOnTop />
              <Global styles={globalStyles} />
              {children}
            </ThemeProvider>
          </FlameTheme>
        </Provider>
      </I18nextProvider>
    </>
  );
};

export default App;
